import type { Theme } from '@mui/material';
import type { IAnonymousListingListWithActivitiesAndBlogListFilters } from 'src/context/activitiesAndBlogs/anonymous_listingListWithActivitiesAndBlogList';

import { useSelector } from 'react-redux';
import { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router';

import {
  Box,
  Stack,
  Button,
  Divider,
  TextField,
  Typography,
  IconButton,
  useMediaQuery,
} from '@mui/material';

import { paths } from 'src/routes/paths';

import { useAppDispatch } from 'src/context/store';
import {
  setParentListingListPagination,
  parent_listingListWithActivityAndBlogListFunc,
} from 'src/context/activitiesAndBlogs/parent_listingListWithActivityAndBlogList';
import {
  setAnonymousListingListPagination,
  anonymous_listingListWithActivitiesAndBlogListFunc,
} from 'src/context/activitiesAndBlogs/anonymous_listingListWithActivitiesAndBlogList';

import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';

import { CityFilter } from './filters/CityFilter';
import { DaysFilter } from './filters/DaysFilter';
import { AgesFilter } from './filters/AgesFilter';
import { DateFilter } from './filters/DateFilter';
import { FormatFilter } from './filters/FormatFilter';
import { CategoryFilter } from './filters/CategoryFilter';

// ----------------------------------------------------------------------

export function ProductFiltersMenu(props: {
  sort?: string;
  setCategoryName: (name: string) => void;
  showMap?: boolean;
}) {
  const downSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const accessTokenJSON = localStorage.getItem('auth') as string;
  const accessToken = JSON.parse(accessTokenJSON)?.accessToken ?? null;

  const dispatch = useAppDispatch();
  const debounceTimer = useRef<NodeJS.Timeout>();
  const activitiesAndBlogs = useSelector((state: any) =>
    accessToken
      ? state.parent_listingListWithActivityAndBlogList
      : state.anonymous_listingListWithActivitiesAndBlogList
  );
  const { page } = useSelector((state: any) =>
    accessToken
      ? state.parent_listingListWithActivityAndBlogList
      : state.anonymous_listingListWithActivitiesAndBlogList
  );
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // SessionStorage ve URL parametrelerinden filtreleri oku
  const getFiltersFromStorage = useCallback(() => {
    // Önce URL parametrelerini kontrol et
    const search = searchParams.get('arama') || null;
    const category = searchParams.get('kategori') || null;
    const city = searchParams.get('sehir') || null;
    const district = searchParams.get('ilce') || null;
    const days = searchParams.get('gunler') || null;
    const ageInterval = searchParams.get('yas') || null;
    const format = searchParams.get('format') || null;
    const fee = searchParams.get('ucret') || null;
    const minPrice = searchParams.get('min') || null;
    const maxPrice = searchParams.get('max') || null;
    const parking = searchParams.get('park') || null;
    const activityBlogFilter = searchParams.get('activityBlogFilter') || null;
    const minDate = searchParams.get('minDate') || null;
    const maxDate = searchParams.get('maxDate') || null;

    const hasUrlParams = !!(
      search ||
      category ||
      city ||
      district ||
      days ||
      ageInterval ||
      format ||
      fee ||
      minPrice ||
      maxPrice ||
      parking ||
      activityBlogFilter ||
      minDate ||
      maxDate
    );

    if (hasUrlParams) {
      const parsedDays = days ? days.split(',').map(Number) : null;

      return {
        sort: props.sort,
        listingSortings: 'mostRecentUpdatedActivityDesc',
        listing: {
          title: search,
          category,
          ageInterval: ageInterval ? ageInterval.split(',').map(Number) : null,
          format: format || null,
        },
        location: {
          district,
          city,
          parking: parking || null,
        },
        schedule: {
          days: parsedDays,
          dateInterval: {
            from: minDate,
            to: maxDate,
          },
        },
        fee: fee || null,
        price: {
          from: minPrice ? parseInt(minPrice, 10) : null,
          to: maxPrice ? parseInt(maxPrice, 10) : null,
        },
        fromQuota: null,
        toQuota: null,
        byCoordinates: null,
        long: null,
        lat: null,
        finished: null,
        page: 1,
        pageSize: props.showMap ? 500 : 15,
        activityBlogFilterAdditionalDetails: activityBlogFilter || null,
      };
    }

    // URL boşsa SessionStorage'ı dene
    try {
      const stored = sessionStorage.getItem('hophop_filters');
      if (stored) {
        const parsedFilters = JSON.parse(stored);
        return {
          ...parsedFilters,
          sort: props.sort,
          listingSortings: parsedFilters.listingSortings || 'mostRecentUpdatedActivityDesc',
          pageSize: props.showMap ? 500 : 15,
        };
      }
    } catch {
      // SessionStorage filtre okuma hatası
    }

    // Varsayılan filtreler
    return {
      sort: props.sort,
      listingSortings: 'mostRecentUpdatedActivityDesc',
      listing: {
        title: null,
        ageInterval: null,
        format: null,
        category: null,
      },
      location: {
        district: null,
        city: null,
        parking: null,
      },
      schedule: {
        days: null,
        dateInterval: {
          from: null,
          to: null,
        },
      },
      fee: null,
      price: {
        from: null,
        to: null,
      },
      fromQuota: null,
      toQuota: null,
      byCoordinates: null,
      long: null,
      lat: null,
      finished: null,
      page: 1,
      pageSize: props.showMap ? 500 : 15,
      activityBlogFilterAdditionalDetails: null,
    };
  }, [props.sort, props.showMap, searchParams]);

  const [filters, setFiltersState] =
    useState<IAnonymousListingListWithActivitiesAndBlogListFilters>(getFiltersFromStorage);

  // SessionStorage'a kaydetmek için custom setFilters
  const setFilters = useCallback(
    (
      newFilters:
        | IAnonymousListingListWithActivitiesAndBlogListFilters
        | ((
            prev: IAnonymousListingListWithActivitiesAndBlogListFilters
          ) => IAnonymousListingListWithActivitiesAndBlogListFilters)
    ) => {
      setFiltersState((prev) => {
        const updatedFilters = typeof newFilters === 'function' ? newFilters(prev) : newFilters;

        // SessionStorage'a kaydet
        try {
          sessionStorage.setItem('hophop_filters', JSON.stringify(updatedFilters));
        } catch {
          // SessionStorage filtre kaydetme hatası
        }

        return updatedFilters;
      });
    },
    []
  );

  // URL parametreleri değiştiğinde tüm filtreleri güncelle
  useEffect(() => {
    const search = searchParams.get('arama') || null;
    const category = searchParams.get('kategori') || null;
    const city = searchParams.get('sehir') || null;
    const district = searchParams.get('ilce') || null;
    const days = searchParams.get('gunler') || null;
    const ageInterval = searchParams.get('yas') || null;
    const format = searchParams.get('format') || null;
    const fee = searchParams.get('ucret') || null;
    const minPrice = searchParams.get('min') || null;
    const maxPrice = searchParams.get('max') || null;
    const parking = searchParams.get('park') || null;
    const activityBlogFilter = searchParams.get('activityBlogFilter') || null;
    const minDate = searchParams.get('minDate') || null;
    const maxDate = searchParams.get('maxDate') || null;

    const hasUrlParams = !!(
      search ||
      category ||
      city ||
      district ||
      days ||
      ageInterval ||
      format ||
      fee ||
      minPrice ||
      maxPrice ||
      parking ||
      activityBlogFilter ||
      minDate ||
      maxDate
    );

    // URL'de parametre varsa filtreleri güncelle
    if (hasUrlParams) {
      const parsedDays = days ? days.split(',').map(Number) : null;

      setFiltersState({
        sort: props.sort,
        listing: {
          title: search,
          category,
          ageInterval: ageInterval ? ageInterval.split(',').map(Number) : null,
          format: format || null,
        },
        location: {
          district,
          city,
          parking: parking || null,
        },
        schedule: {
          days: parsedDays,
          dateInterval: {
            from: minDate,
            to: maxDate,
          },
        },
        fee: fee || null,
        price: {
          from: minPrice ? parseInt(minPrice, 10) : null,
          to: maxPrice ? parseInt(maxPrice, 10) : null,
        },
        fromQuota: null,
        toQuota: null,
        byCoordinates: null,
        long: null,
        lat: null,
        finished: null,
        page: 1,
        pageSize: props.showMap ? 500 : 15,
        activityBlogFilterAdditionalDetails: activityBlogFilter || null,
      });
    }
  }, [location.search, searchParams, props.sort, props.showMap, location.pathname]);

  useEffect(() => {
    if (filters?.listing?.category) {
      props.setCategoryName(filters.listing.category);
    } else {
      props.setCategoryName('Aktiviteler');
    }
  }, [filters?.listing?.category, props]);

  // sort prop değiştiğinde filters.sort'u güncel tut
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      sort: props.sort,
    }));
  }, [props.sort, setFilters]);

  // showMap değiştiğinde pageSize'ı güncelle
  useEffect(() => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      pageSize: props.showMap ? 500 : 15,
    }));
  }, [props.showMap]);

  // SessionStorage temizlenince filtreleri resetle (global layout'tan temizleme gelince)
  useEffect(() => {
    const stored = sessionStorage.getItem('hophop_filters');

    // URL parametresi var mı kontrol et
    const hasUrlParams = !!(
      searchParams.get('arama') ||
      searchParams.get('kategori') ||
      searchParams.get('sehir') ||
      searchParams.get('ilce') ||
      searchParams.get('gunler') ||
      searchParams.get('yas') ||
      searchParams.get('format') ||
      searchParams.get('ucret') ||
      searchParams.get('min') ||
      searchParams.get('max') ||
      searchParams.get('park') ||
      searchParams.get('activityBlogFilter') ||
      searchParams.get('minDate') ||
      searchParams.get('maxDate')
    );

    // URL parametresi varsa resetleme! Çünkü URL'den filtre geliyordur
    if (hasUrlParams) {
      return;
    }

    if (!stored && (filters.listing.title || filters.listing.category || filters.schedule.days)) {
      // SessionStorage temizlenmiş ama local state'te hala filtre var
      // Yalnızca aktivite liste sayfasındaysak local state'i temizle
      if (!/^\/aktivite(\/?|\?.*)?$/.test(location.pathname)) {
        return;
      }

      // Pagination'ı sıfırla
      if (accessToken) {
        dispatch(setParentListingListPagination(1));
      } else {
        dispatch(setAnonymousListingListPagination(1));
      }

      // Filtreleri resetle
      setFilters({
        sort: props.sort,
        listing: {
          title: null,
          ageInterval: null,
          format: null,
          category: null,
        },
        location: {
          district: null,
          city: null,
          parking: null,
        },
        schedule: {
          days: null,
          dateInterval: {
            from: null,
            to: null,
          },
        },
        fee: null,
        price: {
          from: null,
          to: null,
        },
        fromQuota: null,
        toQuota: null,
        byCoordinates: null,
        long: null,
        lat: null,
        finished: null,
        page: 1,
        pageSize: props.showMap ? 500 : 15,
        activityBlogFilterAdditionalDetails: null,
      });
    }
  }, [
    filters,
    dispatch,
    accessToken,
    props.sort,
    props.showMap,
    setFilters,
    location.pathname,
    searchParams,
  ]);

  // Menüden aynı sayfadayken 'Aktiviteler' tekrar tıklanınca global event ile reset et
  useEffect(() => {
    const handleResetFilters = () => {
      // SessionStorage'ı temizle
      try {
        sessionStorage.removeItem('hophop_filters');
      } catch {
        // SessionStorage temizleme hatası
      }

      // URL parametrelerini temizle
      navigate(window.location.pathname, { replace: true });

      // Pagination'ı sıfırla
      if (accessToken) {
        dispatch(setParentListingListPagination(1));
      } else {
        dispatch(setAnonymousListingListPagination(1));
      }

      // Filtreleri resetle
      setFilters({
        sort: props.sort,
        listing: {
          title: null,
          ageInterval: null,
          format: null,
          category: null,
        },
        location: {
          district: null,
          city: null,
          parking: null,
        },
        schedule: {
          days: null,
          dateInterval: {
            from: null,
            to: null,
          },
        },
        fee: null,
        price: {
          from: null,
          to: null,
        },
        fromQuota: null,
        toQuota: null,
        byCoordinates: null,
        long: null,
        lat: null,
        finished: null,
        page: 1,
        pageSize: props.showMap ? 500 : 15,
        activityBlogFilterAdditionalDetails: null,
      });
    };

    window.addEventListener('hophop:reset-filters', handleResetFilters);

    return () => {
      window.removeEventListener('hophop:reset-filters', handleResetFilters);
    };
  }, [accessToken, dispatch, navigate, props.sort, props.showMap, setFilters]);

  useEffect(() => {
    if (dispatch) {
      const apiCall = accessToken
        ? parent_listingListWithActivityAndBlogListFunc
        : anonymous_listingListWithActivitiesAndBlogListFunc;

      dispatch(apiCall({ ...filters, page }));
    }
  }, [dispatch, filters, page, accessToken]);

  const handleSearch = () => {
    if (accessToken) {
      dispatch(setParentListingListPagination(1));
    } else {
      dispatch(setAnonymousListingListPagination(1));
    }

    // Sonra arama yap
    setTimeout(() => {
      if (accessToken) {
        dispatch(parent_listingListWithActivityAndBlogListFunc({ ...filters, page: 1 }));
      } else {
        dispatch(anonymous_listingListWithActivitiesAndBlogListFunc({ ...filters, page: 1 }));
      }
    }, 0);
  };

  // Debounce edilmiş arama fonksiyonu
  const debouncedSearch = useCallback(
    (searchValue?: string) => {
      const titleToUse = searchValue !== undefined ? searchValue : filters.listing.title;

      // Önceki timer'ı temizle
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      // 500ms sonra arama yap
      debounceTimer.current = setTimeout(() => {
        // Güncel filters'ı kullan ama title için parametre değerini kullan
        const updatedFilters = {
          ...filters,
          listing: {
            ...filters.listing,
            title: titleToUse,
          },
        };

        // URL'yi güncelle - mevcut tüm query parametrelerini al
        const category = searchParams.get('kategori') || '';
        const city = searchParams.get('sehir') || '';
        const district = searchParams.get('ilce') || '';
        const days = searchParams.get('gunler') || '';
        const fee = searchParams.get('ucret') || '';
        const minPrice = searchParams.get('min') || '';
        const maxPrice = searchParams.get('max') || '';
        const minDate = searchParams.get('minDate') || '';
        const maxDate = searchParams.get('maxDate') || '';
        const ageInterval = searchParams.get('yas') || '';
        const format = searchParams.get('format') || '';
        const parking = searchParams.get('park') || '';
        const activityBlogFilter = searchParams.get('activityBlogFilter') || '';

        // URL'yi güncelle - arama değeri boşsa URL'den kaldır
        const newUrl = paths.aktivite.list({
          searchKey: titleToUse || '',
          category,
          city,
          district,
          days,
          fee,
          minPrice,
          maxPrice,
          minDate,
          maxDate,
          ageInterval,
          format,
          parking,
          otherParams: activityBlogFilter ? { activityBlogFilter } : {},
        });

        // URL'yi güncelle (replace: true ile history'ye eklemeden)
        navigate(newUrl, { replace: true });

        // Pagination sıfırla ve arama yap
        if (accessToken) {
          dispatch(setParentListingListPagination(1));
          dispatch(parent_listingListWithActivityAndBlogListFunc({ ...updatedFilters, page: 1 }));
        } else {
          dispatch(setAnonymousListingListPagination(1));
          dispatch(
            anonymous_listingListWithActivitiesAndBlogListFunc({ ...updatedFilters, page: 1 })
          );
        }
      }, 500);
    },
    [filters, accessToken, dispatch, navigate, searchParams]
  );

  const renderHead = () => (
    <Box
      sx={{
        py: 2,
        px: 4,
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'primary.main',
      }}
    >
      <Typography variant="h6" fontWeight={400} sx={{ color: 'common.white' }}>
        <strong>
          {accessToken
            ? (activitiesAndBlogs?.parent_listingListWithActivityAndBlogListData?.count ?? 0)
            : (activitiesAndBlogs?.anonymous_listingListWithActivitiesAndBlogListData?.count ?? 0)}
        </strong>{' '}
        aktivite listeleniyor.
      </Typography>
    </Box>
  );

  const renderSearch = () => (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, mt: 2, alignItems: 'center' }}>
        <TextField
          id="input-with-sx"
          label="Arama Yap"
          variant="outlined"
          type="text"
          value={filters.listing.title || ''}
          size="small"
          onChange={(e) => {
            const searchValue = e.target.value ?? '';
            setFilters({
              ...filters,
              listing: { ...filters?.listing, title: searchValue },
            });
            // Debounce edilmiş arama yap (boş string dahil) - yeni değeri parametre olarak geç
            debouncedSearch(searchValue);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSearch();
            }
          }}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-notchedOutline': {
              borderRadius: '100px',
            },
          }}
        />
        <IconButton onClick={handleSearch} sx={{ alignSelf: 'flex-end', mt: 1 }}>
          <Iconify icon="mdi:magnify" color="primary" width={24} height={24} />
        </IconButton>
      </Box>
      <Divider />
    </>
  );

  const renderActions = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Button
        variant="contained"
        color="primary"
        size="large"
        onClick={() => {
          // SessionStorage'ı temizle
          try {
            sessionStorage.removeItem('hophop_filters');
          } catch {
            // SessionStorage temizleme hatası
          }

          // URL parametrelerini temizle
          navigate(window.location.pathname, { replace: true });

          // Pagination'ı sıfırla
          if (accessToken) {
            dispatch(setParentListingListPagination(1));
          } else {
            dispatch(setAnonymousListingListPagination(1));
          }

          // Filtreleri resetle
          setFilters({
            sort: props.sort,
            listing: {
              title: null,
              ageInterval: null,
              format: null,
              category: null,
            },
            location: {
              district: null,
              city: null,
              parking: null,
            },
            schedule: {
              days: null,
              dateInterval: {
                from: null,
                to: null,
              },
            },
            fee: null,
            price: {
              from: null,
              to: null,
            },
            fromQuota: null,
            toQuota: null,
            byCoordinates: null,
            long: null,
            lat: null,
            finished: null,
            page: 1,
            pageSize: props.showMap ? 500 : 15,
            activityBlogFilterAdditionalDetails: null,
          });
        }}
      >
        Filtreyi Resetle
      </Button>
      {/* <Button variant="contained" color="primary" size="large" onClick={handleSearch}>
        Uygula
      </Button> */}
    </Box>
  );

  return (
    <Box
      sx={{
        width: downSm ? '100%' : 280,
        height: 'fit-content',
        overflow: 'hidden',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        mt: 4,
      }}
    >
      {renderHead()}

      <Scrollbar sx={{ px: 2.5, py: 3 }}>
        <Stack spacing={3}>
          {renderSearch()}
          <CategoryFilter filters={filters} setFilters={setFilters} />
          <Divider />
          <CityFilter filters={filters} setFilters={setFilters} />
          <Divider />
          <DaysFilter filters={filters} setFilters={setFilters} />
          <Divider />
          <AgesFilter filters={filters} setFilters={setFilters} />
          <Divider />
          <FormatFilter filters={filters} setFilters={setFilters} />
          <Divider />
          <DateFilter filters={filters} setFilters={setFilters} />
          {/* <Divider />
          <PriceFilter filters={filters} setFilters={setFilters} /> */}
          {/* <Divider />
          <PriceRangeFilter filters={filters} setFilters={setFilters} /> */}
          {/* <Divider /> */}
          {/* <ParkingFilter filters={filters} setFilters={setFilters} /> */}
          {/* {renderActions()} */}
        </Stack>
      </Scrollbar>
    </Box>
  );
}
